import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import { WarningPopUp, Image, Button } from "hesus-ui-kit";
import PulseLoader from "react-spinners/PulseLoader";
import ClipLoader from "react-spinners/ClipLoader";
import NotFound from "@components/Errors/NotFound";
import { RequiredMessagePara } from "@utils/extra-style";
import DocumentSection from "./components/DocumentSection";
import AdditionalDocumentInformation from "./components/AdditionalDocumentInformation";
import InvoiceServices from "./components/InvoiceServices";
import {
  invoiceStatusActionData,
  invoiceStatusOptions,
  invoiceWarning,
} from "@utils/constants";
import editIcon from "@icons/ic-edit-blue.svg";
import moreIcon from "@icons/ic-more.svg";
import downloadIcon from "@icons/ic-download.svg";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import {
  MainViewContainer,
  SectionContainer,
  OuterSection,
  Title,
  GridRow,
  MoreDropdownMenu,
} from "./InvoiceView.styled";
import "../AddEditInvoice/AddEditInvoice.scss";

const InvoiceView = ({ t }) => {
  const params = useParams();
  const history = useNavigate();
  const statusOptions = invoiceStatusOptions(t);
  const generalParams = useGetGeneralParameters();
  const warningContent = invoiceWarning(t);
  const { countryList } = generalParams;
  const invoiceId = parseInt(params.id, 10);
  const [isNotFound, setIsNotFound] = useState(false);
  const [invoiceData, setInvoiceData] = useState([]);
  const [apiError, setApiError] = useState("");
  const [loadingInvoice, setLoadingInvoice] = useState(true);
  const [showActions, setShowActions] = useState(false);
  const [internalComment, setInternalComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isConfirmVisible, setConfirmVisibility] = useState(false);
  const [isConfirm, setActionConfirmStatus] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const [warningTitle, setWarningTitle] = useState("");
  const [warningMessage, setWarningMessage] = useState("");
  const [statusId, setStatusId] = useState("");
  const [serviceItems, setServiceItems] = useState([]);
  const [extraItems, setExtraItems] = useState([]);
  const [serviceLoading, setServiceLoading] = useState(false);

  useEffect(() => {
    if (!invoiceData?.items) {
      setServiceItems([]);
      setExtraItems([]);
      return;
    }
    setServiceLoading(true);
    const groupedServices = invoiceData.items
      .filter((item) => !item.isExtra)
      .reduce((groups, item) => {
        const serviceId = item.quoteService?.id;
        if (!groups[serviceId]) {
          groups[serviceId] = {
            quoteService: item.quoteService,
            quoteItems: [],
          };
        }
        groups[serviceId].quoteItems.push(item);
        return groups;
      }, {});

    setServiceItems(Object.values(groupedServices));
    setExtraItems(invoiceData.items.filter((item) => item.isExtra));
    setServiceLoading(false);
  }, [invoiceData]);

  const fetchInvoiceData = () => {
    setLoadingInvoice(true);
    axios
      .get(`/clientInvoices/${invoiceId} `)
      .then((response) => {
        setInvoiceData(response.data?.[0]);
        setInternalComment(response.data?.[0]?.internalComment);
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          setIsNotFound(true);
          return;
        }
        setApiError(error.response?.data?.message ?? "Failed to load invoice");
      })
      .finally(() => setLoadingInvoice(false));
  };

  useEffect(() => {
    fetchInvoiceData();
  }, []);

  const status = statusOptions.find(
    (option) => option.value === invoiceData?.status
  );

  const actions = invoiceStatusActionData(
    t,
    invoiceData.status,
    invoiceData.documentType,
    invoiceData.isSentByEmail
  );

  // on form update
  const onSubmit = (payload, url, method) => {
    axios[method](url, payload)
      .then((response) => {
        setInvoiceData(response.data?.[0]);
        setInternalComment(response.data?.[0]?.internalComment);
      })
      .catch((error) => {
        console.error("Submit error:", error);
      })
      .finally(() => {
        setIsSubmitting(false);
        setStatusUpdating(false);
        setActionConfirmStatus(false);
        setWarningTitle("");
        setWarningMessage("");
      });
  };
  const onCommentUpdate = () => {
    setIsSubmitting(true);
    const payload = {
      internalComment: internalComment,
    };
    const url = `/clientInvoices/${invoiceData?.id}`;
    onSubmit(payload, url, "put");
  };

  useEffect(() => {
    if (isConfirm) {
      const payload = {
        previousStatusId: invoiceData.status,
        newStatusId: statusId,
      };
      setStatusUpdating(true);
      const url = `/clientInvoices/${invoiceData?.id}/transitionStatus`;
      onSubmit(payload, url, "post");
    }
  }, [isConfirm]);

  const onStatusUpdate = (newStatusId) => {
    newStatusId = parseInt(newStatusId, 10);
    setStatusId(newStatusId);
    setShowActions(false);
    if (!Object.hasOwn(warningContent, newStatusId)) {
      setActionConfirmStatus(true);
    } else {
      const warningOption = warningContent[newStatusId];
      setWarningTitle(warningOption.title);
      setWarningMessage(warningOption.message);
      setConfirmVisibility(true);
    }
  };

  const onPopUpCancel = () => {
    setConfirmVisibility(false);
    setActionConfirmStatus(false);
  };

  const onPopUpConfirm = () => {
    setConfirmVisibility(false);
    setActionConfirmStatus(true);
  };

  if (isNotFound) {
    return <NotFound />;
  }

  return (
    <MainViewContainer className="view-invoice">
      <SectionContainer>
        <OuterSection $isLoading={loadingInvoice}>
          {loadingInvoice && (
            <GridRow>
              <div className="suggestions-list-loading">
                <PulseLoader size={14} color="#1bace4" />
              </div>
            </GridRow>
          )}
          {!loadingInvoice && Object.keys(generalParams).length > 0 && (
            <>
              <GridRow column="1fr auto " padding="6px 0px">
                <Title>
                  {`${
                    invoiceData.documentType === 1
                      ? t("CLIENT_INVOICING_DOCUMENT_TYPE_INVOICE")
                      : t("CLIENT_INVOICING_DOCUMENT_TYPE_CREDIT_NOTE")
                  } #${invoiceData.id} ${
                    invoiceData.documentNumber
                      ? "/" + invoiceData.documentNumber
                      : ""
                  }`}
                </Title>
                <div style={{ position: "relative" }}>
                  {status && (
                    <Button
                      styles={{
                        padding: "4px 10px",
                        color: "white",
                        background: status.bgColor,
                        border: `1px solid ${status.bgColor}`,
                        "&:hover": {
                          backgroundColor: status.bgColor,
                        },
                      }}
                      onClick={onCommentUpdate}
                    >
                      {status?.label}
                    </Button>
                  )}
                  {apiError && (
                    <RequiredMessagePara>{apiError}</RequiredMessagePara>
                  )}
                  {[0, 1, 10].includes(Number(invoiceData?.status)) && (
                    <Image
                      src={editIcon}
                      alt={t("GENERIC_ACTION_TOOLTIP_EDIT")}
                      title={t("GENERIC_ACTION_TOOLTIP_EDIT")}
                      className="view-invoice__image-view-icon"
                      onClick={() => history(`/invoice/edit/${invoiceId}`)}
                    />
                  )}
                  <Image
                    src={downloadIcon}
                    alt={t("GENERIC_ACTION_TOOLTIP_DOWNLOAD")}
                    title={t("GENERIC_ACTION_TOOLTIP_DOWNLOAD")}
                    className="view-invoice__image-view-icon"
                  />
                  {actions.length > 0 && (
                    <>
                      {!statusUpdating && (
                        <Image
                          src={moreIcon}
                          alt={t("GENERIC_ACTION_TOOLTIP_MORE_ACTIONS")}
                          title={t("GENERIC_ACTION_TOOLTIP_MORE_ACTIONS")}
                          className="view-invoice__image-view-icon"
                          onClick={() => setShowActions(!showActions)}
                        />
                      )}
                      {statusUpdating && (
                        <ClipLoader size={15} color="#1bace4" />
                      )}
                      <MoreDropdownMenu
                        display={showActions ? "block" : "none"}
                      >
                        {showActions && (
                          <ul className="view-invoice__more-dropdown-menu-list">
                            {actions.map((action) => (
                              <li
                                key={action.value}
                                className="view-invoice__more-dropdown-item"
                                onClick={() => {
                                  onStatusUpdate(action.value);
                                }}
                              >
                                {t(action.label)}
                              </li>
                            ))}
                          </ul>
                        )}
                      </MoreDropdownMenu>
                    </>
                  )}
                </div>
              </GridRow>
              <DocumentSection
                invoiceData={invoiceData}
                countryList={countryList}
              />
              <AdditionalDocumentInformation
                invoiceData={invoiceData}
                internalComment={internalComment}
                setInternalComment={setInternalComment}
                isSubmitting={isSubmitting}
                onCommentUpdate={onCommentUpdate}
              />

              <InvoiceServices
                serviceItems={serviceItems}
                extraItems={extraItems}
                serviceLoading={serviceLoading}
                invoiceData={invoiceData}
              />
              <GridRow background="none"></GridRow>
              <GridRow background="none"></GridRow>
            </>
          )}
          {isConfirmVisible && (
            <WarningPopUp
              title={warningTitle}
              message={warningMessage}
              onPopUpCancel={onPopUpCancel}
              onPopUpConfirm={onPopUpConfirm}
              ValidateContent={t("VALIDATE")}
              CancelContent={t("CANCEL")}
            />
          )}
        </OuterSection>
      </SectionContainer>
    </MainViewContainer>
  );
};

InvoiceView.propTypes = {
  t: PropTypes.func.isRequired,
};

InvoiceView.defaultProps = {};

export default withTranslation()(InvoiceView);
